import type { ContentItem } from '../api/apiClient'
import { getApiBase, resolveMediaUrl } from './media'

export type NewsCardItem = {
  id: string
  title: string
  summary?: string
  category?: string
  dateLabel?: string
  link?: string
  to?: string
  ctaLabel?: string
  image?: string
  sourceName?: string
  slug?: string
}

const newsDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

const getStringField = (item: ContentItem, keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = item[key]
    if (typeof value === 'string' && value.trim().length > 0) {
      return value
    }
  }
  return undefined
}

export const formatContentDate = (iso?: string | null): string | undefined => {
  if (!iso) return undefined
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }
  return newsDateFormatter.format(date)
}

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2)
}

export const toNewsCardItem = (item: ContentItem, fallbackRoute = '/news'): NewsCardItem => {
  const apiBase = getApiBase()
  const summary = item.summary ?? item.excerpt ?? ''
  const ctaUrl = getStringField(item, ['cta_url', 'ctaUrl', 'external_url', 'externalUrl', 'link_url', 'linkUrl'])
  const ctaLabel = getStringField(item, ['cta_label', 'ctaLabel']) ?? (ctaUrl ? 'Read update' : undefined)

  const heroImage = getStringField(item, [
    'hero_image',
    'heroImage',
    'featured_image',
    'featuredImage',
    'thumbnail_image',
    'thumbnailImage',
    'promo_image',
    'promoImage',
  ])

  const resolvedImage = resolveMediaUrl(apiBase, heroImage) ?? heroImage

  return {
    id: String(item.id ?? item.slug ?? generateId()),
    slug: item.slug,
    title: item.title ?? 'Update pending',
    summary: summary || 'More details arriving soon.',
    category: item.category ?? item.type ?? item.tags?.[0] ?? 'Update',
    dateLabel: formatContentDate(item.published_at ?? item.updated_at),
    link: ctaUrl,
    to: ctaUrl ? undefined : fallbackRoute,
    ctaLabel,
    image: resolvedImage,
    sourceName: item.source_name ?? getStringField(item, ['sourceName', 'source']) ?? undefined,
  }
}

export const getContentHeroImage = (item: ContentItem): string | undefined => {
  const apiBase = getApiBase()
  const heroImage = getStringField(item, [
    'hero_image',
    'heroImage',
    'featured_image',
    'featuredImage',
    'promo_image',
    'promoImage',
    'thumbnail_image',
    'thumbnailImage',
  ])

  return resolveMediaUrl(apiBase, heroImage) ?? heroImage
}

export const collectContentTags = (items: ContentItem[]): string[] => {
  const tags = new Set<string>()
  items.forEach((item) => {
    item.tags?.forEach((tag) => {
      if (tag) {
        tags.add(tag)
      }
    })
  })
  return Array.from(tags).sort((a, b) => a.localeCompare(b))
}

const readFlexibleField = (record: Record<string, unknown> | null | undefined, keys: string[]): unknown => {
  if (!record) {
    return undefined
  }
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      return (record as Record<string, unknown>)[key]
    }
  }
  return undefined
}

export const readExpiryDateFromContent = (item: ContentItem): Date | null => {
  const dynamicItem = item as Record<string, unknown>
  const directRaw =
    readFlexibleField(dynamicItem, ['expiry_date', 'expiryDate', 'expires_at', 'expiresAt']) ?? null
  const metadata = item.metadata && typeof item.metadata === 'object' ? (item.metadata as Record<string, unknown>) : null
  const metadataRaw =
    readFlexibleField(metadata, ['expiry_date', 'expiryDate', 'expires_at', 'expiresAt']) ?? null
  const raw = (directRaw ?? metadataRaw) as unknown
  if (!raw) {
    return null
  }
  const candidate = typeof raw === 'number' ? new Date(raw) : new Date(String(raw))
  if (Number.isNaN(candidate.getTime())) {
    return null
  }
  return candidate
}

export const isContentExpired = (item: ContentItem, reference: Date | number = Date.now()): boolean => {
  const expiryDate = readExpiryDateFromContent(item)
  if (!expiryDate) {
    return false
  }
  const referenceTime = reference instanceof Date ? reference.getTime() : reference
  return expiryDate.getTime() < referenceTime
}

export const getContentPlaceSlug = (item: ContentItem): string | null => {
  const lookupSources: Array<Record<string, unknown> | null | undefined> = [item as Record<string, unknown>, item.metadata]
  for (const source of lookupSources) {
    if (!source) {
      continue
    }
    const slugCandidate = readFlexibleField(source, ['place_slug', 'placeSlug'])
    if (typeof slugCandidate === 'string' && slugCandidate.trim().length > 0) {
      return slugCandidate.trim()
    }
    const nestedPlace = readFlexibleField(source, ['place'])
    if (nestedPlace && typeof nestedPlace === 'object') {
      const nestedSlug = readFlexibleField(nestedPlace as Record<string, unknown>, ['slug'])
      if (typeof nestedSlug === 'string' && nestedSlug.trim().length > 0) {
        return nestedSlug.trim()
      }
    }
  }
  if (Array.isArray(item.related_places) && item.related_places.length > 0) {
    const match = item.related_places.find((entry) => typeof entry === 'string' && entry.trim().length > 0)
    if (match) {
      return match.trim()
    }
  }
  return null
}
